import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Dumbbell, Clock, Flame, Loader2, Sparkles, Scale, 
  ChevronRight, Check, Calendar, History, RotateCcw, Save, Trash2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const PersonalizedWorkout = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('plan');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [previewPlan, setPreviewPlan] = useState(null); // Unsaved generated plan
  const [workoutHistory, setWorkoutHistory] = useState([]);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  
  const [formData, setFormData] = useState({
    goal: '',
    weight: '',
    fitnessLevel: 'beginner',
    daysPerWeek: '4',
    equipment: 'none'
  });

  const fitnessLevels = [
    { value: 'beginner', label: 'Beginner', icon: '🌱' },
    { value: 'intermediate', label: 'Intermediate', icon: '💪' },
    { value: 'advanced', label: 'Advanced', icon: '🔥' }
  ];

  const daysOptions = ['3', '4', '5', '6'];

  const equipmentOptions = [
    { value: 'none', label: 'Bodyweight', icon: '🏃' },
    { value: 'basic', label: 'Basic', icon: '🏋️' },
    { value: 'full', label: 'Full Gym', icon: '🎯' }
  ];

  const quickGoals = [
    'Weight loss with yoga',
    'Build muscle', 
    'HIIT for fat burn',
    'Improve flexibility', 
    'Strength training',
    'Cardio fitness'
  ];

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const shortDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    if (isOpen) {
      loadCurrentPlan();
      loadWorkoutHistory();
    }
  }, [isOpen]);

  const loadCurrentPlan = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/workout-plan`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.plan) {
          setCurrentPlan(data.plan);
          setPreviewPlan(null);
          setShowCreateNew(false);
        } else {
          setShowCreateNew(true);
        }
      }
    } catch (error) {
      console.error('Error loading workout plan:', error);
      setShowCreateNew(true);
    }
  };

  const loadWorkoutHistory = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/workout-history`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setWorkoutHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error loading workout history:', error);
    }
  };

  const generateWeeklyPlan = async () => {
    if (!formData.goal.trim()) {
      toast({
        title: 'Please describe your goal',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/generate-weekly-plan`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            goal: formData.goal,
            weight: formData.weight ? parseFloat(formData.weight) : null,
            fitness_level: formData.fitnessLevel,
            days_per_week: parseInt(formData.daysPerWeek),
            equipment: formData.equipment,
            save_plan: false // Don't save yet, just preview
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreviewPlan(data.plan); // Store as preview
        toast({
          title: '✨ Plan Generated!',
          description: 'Review your plan and save it when ready.'
        });
      } else {
        throw new Error('Failed to generate plan');
      }
    } catch (error) {
      console.error('Error generating plan:', error);
      toast({
        title: 'Error',
        description: 'Failed to create workout plan. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveWorkoutPlan = async () => {
    if (!previewPlan) return;
    
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/save-workout-plan`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ plan: previewPlan })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCurrentPlan(data.plan);
        setPreviewPlan(null);
        setShowCreateNew(false);
        toast({
          title: '✅ Plan Saved!',
          description: 'Your workout routine is now active.'
        });
      } else {
        throw new Error('Failed to save plan');
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      toast({
        title: 'Error',
        description: 'Failed to save workout plan. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteWorkoutHistory = async (sessionId) => {
    setDeletingId(sessionId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/workout-history/${sessionId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        setWorkoutHistory(prev => prev.filter(w => w.id !== sessionId));
        toast({
          title: 'Deleted',
          description: 'Workout session removed from history.'
        });
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting workout:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete workout session.',
        variant: 'destructive'
      });
    } finally {
      setDeletingId(null);
    }
  };

  const toggleExercise = (index) => {
    setCompletedExercises(prev => 
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const completeWorkout = async () => {
    if (!selectedDay || !currentPlan) return;
    
    const dayWorkout = currentPlan.days[selectedDay];
    
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/health/workout-sessions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            date: new Date().toISOString().split('T')[0],
            workout_name: dayWorkout.name,
            workout_type: dayWorkout.focus,
            duration_minutes: dayWorkout.duration,
            calories_burned: dayWorkout.calories,
            intensity: formData.fitnessLevel,
            exercises: dayWorkout.exercises,
            day_of_week: selectedDay
          })
        }
      );

      toast({
        title: '🎉 Workout Complete!',
        description: `Great job! ${dayWorkout.calories} calories burned!`
      });
      
      setSelectedDay(null);
      setCompletedExercises([]);
      loadWorkoutHistory();
    } catch (error) {
      toast({ title: 'Workout saved!', description: 'Great job!' });
    }
  };

  const handleClose = () => {
    setSelectedDay(null);
    setCompletedExercises([]);
    setPreviewPlan(null);
    onClose();
  };

  const getTodayIndex = () => {
    const today = new Date().getDay();
    return today === 0 ? 6 : today - 1;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // The plan to display (preview or saved)
  const displayPlan = previewPlan || currentPlan;

  // Show day workout detail
  if (selectedDay !== null && currentPlan?.days?.[selectedDay]) {
    const dayWorkout = currentPlan.days[selectedDay];
    const progress = dayWorkout.exercises 
      ? Math.round((completedExercises.length / dayWorkout.exercises.length) * 100) 
      : 0;

    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <button onClick={() => setSelectedDay(null)} className="p-1 hover:bg-gray-100 rounded">
                <ChevronRight className="h-5 w-5 rotate-180" />
              </button>
              {dayNames[selectedDay]} - {dayWorkout.name}
            </DialogTitle>
          </DialogHeader>

          {/* Progress */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-500">Progress</span>
              <span className="font-medium text-teal-600">{progress}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-teal-500 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <Clock className="h-4 w-4 mx-auto text-gray-400 mb-1" />
              <p className="font-semibold text-gray-800">{dayWorkout.duration} min</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-3 text-center">
              <Flame className="h-4 w-4 mx-auto text-orange-500 mb-1" />
              <p className="font-semibold text-gray-800">{dayWorkout.calories} cal</p>
            </div>
            <div className="bg-teal-50 rounded-xl p-3 text-center">
              <Dumbbell className="h-4 w-4 mx-auto text-teal-500 mb-1" />
              <p className="font-semibold text-gray-800">{dayWorkout.focus}</p>
            </div>
          </div>

          {/* Exercises */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {dayWorkout.exercises?.map((exercise, index) => (
              <button
                key={index}
                onClick={() => toggleExercise(index)}
                className={`w-full p-3 rounded-xl text-left transition-all border ${
                  completedExercises.includes(index)
                    ? 'bg-teal-50 border-teal-200'
                    : 'bg-white border-gray-100 hover:border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    completedExercises.includes(index) 
                      ? 'bg-teal-500 text-white' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {completedExercises.includes(index) ? <Check className="h-4 w-4" /> : index + 1}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium text-sm ${completedExercises.includes(index) ? 'text-teal-700 line-through' : 'text-gray-800'}`}>
                      {exercise.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {exercise.sets && `${exercise.sets} sets × `}{exercise.reps || exercise.duration}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Complete Button */}
          <Button 
            onClick={completeWorkout}
            disabled={completedExercises.length === 0}
            className={`w-full h-11 mt-4 rounded-xl ${
              completedExercises.length > 0 ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-200'
            }`}
          >
            <Check className="h-4 w-4 mr-2" />
            Complete Workout
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-800">
            <div className="p-2 bg-teal-50 rounded-xl">
              <Dumbbell className="h-5 w-5 text-teal-600" />
            </div>
            My Workouts
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="plan" className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Weekly Plan
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-1">
              <History className="h-4 w-4" />
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="plan" className="space-y-4">
            {/* Show Preview Plan if generated but not saved */}
            {previewPlan && (
              <div className="space-y-4">
                {/* Preview Banner */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-800">Preview Mode</p>
                      <p className="text-xs text-amber-600">Save this plan to make it active</p>
                    </div>
                    <Sparkles className="h-5 w-5 text-amber-500" />
                  </div>
                </div>
                
                {/* Plan Info */}
                <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                  <h3 className="font-semibold text-teal-800">{previewPlan.name}</h3>
                  <p className="text-sm text-teal-700">{previewPlan.goal}</p>
                  <p className="text-xs text-teal-600 mt-1">
                    {previewPlan.days_per_week} days/week • {previewPlan.fitness_level}
                  </p>
                </div>

                {/* Weekly Schedule Preview */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Weekly Schedule</p>
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {dayNames.map((day, index) => {
                      const dayWorkout = previewPlan.days?.[index];
                      const isRest = !dayWorkout || dayWorkout.isRest;
                      
                      return (
                        <div
                          key={day}
                          className={`p-3 rounded-xl border flex items-center gap-3 ${
                            isRest ? 'opacity-60 bg-gray-50' : 'bg-white'
                          } border-gray-100`}
                        >
                          <div className="w-10 text-center text-gray-500">
                            {shortDays[index]}
                          </div>
                          <div className="flex-1">
                            {isRest ? (
                              <p className="text-sm text-gray-400">Rest Day 😴</p>
                            ) : (
                              <>
                                <p className="font-medium text-sm text-gray-800">
                                  {dayWorkout.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {dayWorkout.duration} min • {dayWorkout.focus}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => setPreviewPlan(null)}
                    className="flex-1 h-11 rounded-xl"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Regenerate
                  </Button>
                  <Button 
                    onClick={saveWorkoutPlan}
                    disabled={saving}
                    className="flex-1 h-11 bg-teal-600 hover:bg-teal-700 rounded-xl"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Plan
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Show Saved Plan */}
            {!previewPlan && !showCreateNew && currentPlan && (
              <>
                {/* Current Plan Info */}
                <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-teal-800">{currentPlan.name}</h3>
                    <button
                      onClick={() => {
                        setShowCreateNew(true);
                        setPreviewPlan(null);
                      }}
                      className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"
                    >
                      <RotateCcw className="h-3 w-3" />
                      New Plan
                    </button>
                  </div>
                  <p className="text-sm text-teal-700">{currentPlan.goal}</p>
                  <p className="text-xs text-teal-600 mt-1">
                    {currentPlan.days_per_week} days/week • {currentPlan.fitness_level}
                  </p>
                </div>

                {/* Weekly Schedule */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">This Week</p>
                  <div className="space-y-2">
                    {dayNames.map((day, index) => {
                      const dayWorkout = currentPlan.days?.[index];
                      const isToday = index === getTodayIndex();
                      const isRest = !dayWorkout || dayWorkout.isRest;
                      
                      return (
                        <button
                          key={day}
                          onClick={() => !isRest && setSelectedDay(index)}
                          disabled={isRest}
                          className={`w-full p-3 rounded-xl text-left transition-all border flex items-center gap-3 ${
                            isToday ? 'border-teal-300 bg-teal-50' : 'border-gray-100 bg-white'
                          } ${isRest ? 'opacity-60' : 'hover:border-gray-200'}`}
                        >
                          <div className={`w-10 text-center ${isToday ? 'text-teal-600 font-semibold' : 'text-gray-500'}`}>
                            {shortDays[index]}
                          </div>
                          <div className="flex-1">
                            {isRest ? (
                              <p className="text-sm text-gray-400">Rest Day 😴</p>
                            ) : (
                              <>
                                <p className={`font-medium text-sm ${isToday ? 'text-teal-700' : 'text-gray-800'}`}>
                                  {dayWorkout.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {dayWorkout.duration} min • {dayWorkout.focus}
                                </p>
                              </>
                            )}
                          </div>
                          {!isRest && (
                            isToday ? (
                              <span className="text-xs bg-teal-500 text-white px-2 py-1 rounded-full">Today</span>
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-300" />
                            )
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Create New Plan Form */}
            {!previewPlan && (showCreateNew || (!currentPlan && !previewPlan)) && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Create your personalized weekly workout routine</p>
                
                {/* Quick Goals */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">What&apos;s your goal?</p>
                  <div className="flex flex-wrap gap-2">
                    {quickGoals.map((qg) => (
                      <button
                        key={qg}
                        onClick={() => setFormData({ ...formData, goal: qg })}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          formData.goal === qg
                            ? 'bg-teal-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {qg}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Goal */}
                <Textarea
                  placeholder="Or describe your specific goal (e.g., 'Weight loss with yoga', 'Build muscle with dumbbells')..."
                  value={formData.goal}
                  onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                  rows={2}
                  className="resize-none"
                />

                {/* Fitness Level */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Fitness Level</p>
                  <div className="grid grid-cols-3 gap-2">
                    {fitnessLevels.map((level) => (
                      <button
                        key={level.value}
                        onClick={() => setFormData({ ...formData, fitnessLevel: level.value })}
                        className={`p-3 rounded-xl text-center transition-all border-2 ${
                          formData.fitnessLevel === level.value
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <span className="text-xl block mb-1">{level.icon}</span>
                        <span className="text-xs font-medium text-gray-700">{level.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Days Per Week */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Days per week</p>
                  <div className="grid grid-cols-4 gap-2">
                    {daysOptions.map((days) => (
                      <button
                        key={days}
                        onClick={() => setFormData({ ...formData, daysPerWeek: days })}
                        className={`p-2.5 rounded-xl text-center transition-all border-2 ${
                          formData.daysPerWeek === days
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <span className="text-sm font-medium text-gray-700">{days} days</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Equipment */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Equipment</p>
                  <div className="grid grid-cols-3 gap-2">
                    {equipmentOptions.map((eq) => (
                      <button
                        key={eq.value}
                        onClick={() => setFormData({ ...formData, equipment: eq.value })}
                        className={`p-3 rounded-xl text-center transition-all border-2 ${
                          formData.equipment === eq.value
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <span className="text-xl block mb-1">{eq.icon}</span>
                        <span className="text-xs font-medium text-gray-700">{eq.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Weight */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <Scale className="h-5 w-5 text-gray-400" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Weight (optional)</p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder="70"
                        value={formData.weight}
                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                        className="w-20 h-8 text-sm border-0 bg-transparent p-0"
                      />
                      <span className="text-sm text-gray-400">kg</span>
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <div className="flex gap-2">
                  {currentPlan && (
                    <Button 
                      variant="outline"
                      onClick={() => setShowCreateNew(false)}
                      className="flex-1 h-11 rounded-xl"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button 
                    onClick={generateWeeklyPlan}
                    disabled={loading}
                    className="flex-1 h-11 bg-teal-600 hover:bg-teal-700 rounded-xl"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Plan
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-3">
            {workoutHistory.length === 0 ? (
              <div className="text-center py-8">
                <History className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No workout history yet</p>
                <p className="text-sm text-gray-400">Complete a workout to see it here</p>
              </div>
            ) : (
              workoutHistory.map((workout, index) => (
                <div key={workout.id || index} className="p-3 bg-white rounded-xl border border-gray-100 group">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-800">{workout.workout_name}</p>
                      <p className="text-xs text-gray-500">{formatDate(workout.date)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm font-semibold text-orange-500">{workout.calories_burned} cal</p>
                        <p className="text-xs text-gray-400">{workout.duration_minutes} min</p>
                      </div>
                      {workout.id && (
                        <button
                          onClick={() => deleteWorkoutHistory(workout.id)}
                          disabled={deletingId === workout.id}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        >
                          {deletingId === workout.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default PersonalizedWorkout;
